import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context';
import {
  accountCardsWithPassword,
  removeCard,
  setPurchasePassword,
  resetPurchasePassword,
} from '../../api/paymentPassword';
import { ActivityIndicator, Button, PasswordDialog } from '../../components/ui';
import type { PasswordDialogMode } from '../../components/ui/PasswordDialog';
import { images } from '../../config/colors';
import tocBackgroundFallback from '../../assets/tocBackground.jpg';
import styles from './PaymentMethodsPage.module.css';

const tocBackground = images.background || tocBackgroundFallback;

interface Card {
  access_key: string;
  last_four: string;
}

export function PaymentMethodsPage() {
  const { loginKey } = useAuth();
  const [loading, setLoading] = useState(false);
  const [cards, setCards] = useState<Card[]>([]);
  const [requirePassword, setRequirePassword] = useState(false);
  const [showActions, setShowActions] = useState(false);

  const [dialogVisible, setDialogVisible] = useState(false);
  const [dialogMode, setDialogMode] = useState<PasswordDialogMode>('password_add');
  const [dialogError, setDialogError] = useState('');
  const [pendingDeleteKey, setPendingDeleteKey] = useState('');

  const loadCards = useCallback(async () => {
    if (!loginKey) return;

    setLoading(true);
    const response = await accountCardsWithPassword(loginKey);
    setLoading(false);

    if (!response.ok) {
      alert("Couldn't retrieve card information.");
      return;
    }

    if (response.data) {
      setCards(response.data.cards || []);
      setRequirePassword(response.data.require_password === true);
    }
  }, [loginKey]);

  useEffect(() => {
    loadCards();
  }, [loadCards]);

  const handleDeleteCard = (accessKey: string) => {
    if (requirePassword) {
      setPendingDeleteKey(accessKey);
      setDialogMode('card_remove');
      setDialogError('');
      setDialogVisible(true);
    } else {
      if (window.confirm('Remove this saved card?')) {
        doRemoveCard(accessKey);
      }
    }
  };

  const doRemoveCard = async (accessKey: string, password?: string) => {
    if (!loginKey) return;

    setLoading(true);
    const response = await removeCard(loginKey, accessKey, password);
    setLoading(false);

    if (response.ok && response.data?.success) {
      setCards((prev) => prev.filter((c) => c.access_key !== accessKey));
    } else {
      alert(response.data?.error?.message || 'Failed to remove card.');
    }
  };

  const openDialog = (mode: PasswordDialogMode) => {
    setDialogMode(mode);
    setDialogError('');
    setDialogVisible(true);
    setShowActions(false);
  };

  const handleDialogSubmit = async (passwords: { currentPassword?: string; newPassword?: string }) => {
    if (!loginKey) return;

    if (dialogMode === 'card_remove') {
      setDialogVisible(false);
      await doRemoveCard(pendingDeleteKey, passwords.currentPassword);
      setPendingDeleteKey('');
      return;
    }

    setLoading(true);

    if (dialogMode === 'password_add') {
      const response = await setPurchasePassword(loginKey, passwords.newPassword || '');
      setLoading(false);

      if (response.ok && response.data?.success) {
        setRequirePassword(true);
        setDialogVisible(false);
        alert('Password added successfully.');
      } else {
        setDialogError(response.data?.error?.message || 'Failed to add password.');
      }
    } else if (dialogMode === 'password_change') {
      const response = await setPurchasePassword(loginKey, passwords.newPassword || '', passwords.currentPassword);
      setLoading(false);

      if (response.ok && response.data?.success) {
        setDialogVisible(false);
        alert('Password changed successfully.');
      } else {
        setDialogError(response.data?.error?.message || 'Failed to change password.');
      }
    } else if (dialogMode === 'password_remove') {
      const response = await setPurchasePassword(loginKey, '', passwords.currentPassword);
      setLoading(false);

      if (response.ok && response.data?.success) {
        setRequirePassword(false);
        setDialogVisible(false);
        alert('Password removed successfully.');
      } else {
        setDialogError(response.data?.error?.message || 'Failed to remove password.');
      }
    }
  };

  const handleDialogCancel = () => {
    setDialogVisible(false);
    setDialogError('');
    setPendingDeleteKey('');
  };

  const handleReset = async () => {
    if (!loginKey) return;

    if (!window.confirm('Reset your purchase password? A new temporary password will be emailed to you.')) {
      return;
    }

    setLoading(true);
    const response = await resetPurchasePassword(loginKey);
    setLoading(false);

    if (response.ok && response.data?.success) {
      alert('A temporary password has been sent to your email.');
    } else {
      alert('Failed to reset password. Please try again.');
    }
  };

  return (
    <div
      className={styles.background}
      style={{ backgroundImage: `url(${tocBackground})` }}
    >
      <ActivityIndicator visible={loading} />

      <PasswordDialog
        visible={dialogVisible}
        mode={dialogMode}
        onSubmit={handleDialogSubmit}
        onCancel={handleDialogCancel}
        error={dialogError}
      />

      <div className={styles.container}>
        <p className={styles.title}>Payment Methods</p>

        <p className={styles.sectionTitle}>Saved Cards</p>
        {cards.length > 0 ? (
          <div className={styles.cardList}>
            {cards.map((card) => (
              <div key={card.access_key} className={styles.cardItem}>
                <span className={styles.cardNumber}>**** **** **** {card.last_four}</span>
                <button
                  className={styles.deleteButton}
                  onClick={() => handleDeleteCard(card.access_key)}
                >
                  &times;
                </button>
              </div>
            ))}
          </div>
        ) : (
          <p className={styles.noCards}>No saved cards.</p>
        )}

        <hr className={styles.divider} />

        <p className={styles.sectionTitle}>Digital Coin Purchases</p>
        <div className={styles.passwordStatus}>
          <p className={`${styles.statusText} ${requirePassword ? styles.statusEnabled : styles.statusDisabled}`}>
            {requirePassword ? 'Password Required' : 'No Password'}
          </p>
        </div>

        {!requirePassword ? (
          <div className={styles.addPasswordButton}>
            <Button onClick={() => openDialog('password_add')}>Add Password</Button>
          </div>
        ) : !showActions ? (
          <div className={styles.addPasswordButton}>
            <Button theme="outline" onClick={() => setShowActions(true)}>Change</Button>
          </div>
        ) : (
          <div className={styles.actionButtons}>
            <div className={styles.actionRow}>
              <Button onClick={() => openDialog('password_change')}>Change</Button>
              <Button theme="outline" onClick={() => openDialog('password_remove')}>Remove</Button>
            </div>
            <div className={styles.actionRow}>
              <Button theme="outline" onClick={handleReset}>Reset</Button>
              <Button theme="outline" onClick={() => setShowActions(false)}>Cancel</Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default PaymentMethodsPage;
